import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import * as client from './api'
import type { SessionUser } from './api'

/**
 * Permission strings, mirroring `app/core/rbac.py`.
 *
 * The *values* are mirrored; the role→permission mapping is not. The console
 * fetches that from `GET /v1/admin/roles` at sign-in so there is exactly one
 * source of truth for who can do what, and adding a role server-side does not
 * need a console release.
 *
 * Everything here is presentation only — it decides which nav items and buttons
 * render. Authorisation happens in the API; a console that hid nothing would be
 * annoying, but not insecure.
 */
export const P = {
  selfRead: 'self:read',
  selfWrite: 'self:write',
  userReadAny: 'user:read:any',
  userWriteAny: 'user:write:any',
  userSuspend: 'user:suspend',
  phiReadAny: 'phi:read:any',
  phiExportAny: 'phi:export:any',
  phiResearch: 'phi:research',
  emergencyReadAny: 'emergency:read:any',
  emergencyDispatch: 'emergency:dispatch',
  aiUse: 'ai:use',
  aiUsageRead: 'ai:usage:read',
  aiPromptRead: 'ai:prompt:read',
  aiModelWrite: 'ai:model:write',
  aiRoutingWrite: 'ai:routing:write',
  aiCredentialWrite: 'ai:credential:write',
  // Drawing a flow and being allowed to fire it are separate decisions —
  // a flow is a standing instruction that can run against real people's data.
  aiAgentWrite: 'ai:agent:write',
  aiAgentRun: 'ai:agent:run',
  privacyPolicyWrite: 'privacy:policy:write',
  auditRead: 'audit:read',
  // Checking that someone claiming to be a dietitian is one. Not user
  // administration and not billing — see the backend's Permission enum.
  marketplaceModerate: 'marketplace:moderate',
  billingRead: 'billing:read',
  billingWrite: 'billing:write',
  configWrite: 'config:write',
  roleWrite: 'role:write',
  adminRead: 'admin:read',
} as const

export type Permission = (typeof P)[keyof typeof P]

type AuthState = {
  ready: boolean
  user: SessionUser | null
  permissions: ReadonlySet<string>
  /** True when the account authenticated but holds no console permission. */
  lockedOut: boolean
  can: (...required: Permission[]) => boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

async function permissionsFor(user: SessionUser): Promise<Set<string>> {
  try {
    const matrix = await client.get<Record<string, string[]>>('/v1/admin/roles')
    const granted = new Set<string>()
    for (const role of user.roles) for (const p of matrix[role] ?? []) granted.add(p)
    return granted
  } catch (error) {
    // A 403 here means the account is not an admin at all — it cannot even read
    // the role matrix. That is a lockout, not a failure to fetch.
    if (error instanceof client.ApiError && error.isForbidden) return new Set()
    throw error
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [user, setUser] = useState<SessionUser | null>(null)
  const [permissions, setPermissions] = useState<ReadonlySet<string>>(new Set())

  const adopt = useCallback(async (session: SessionUser) => {
    setUser(session)
    setPermissions(await permissionsFor(session))
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const tokens = await client.restore()
        if (!cancelled && tokens) await adopt(tokens.user)
      } catch {
        client.clearSession()
      } finally {
        if (!cancelled) setReady(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [adopt])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const tokens = await client.login(email, password)
      await adopt(tokens.user)
    },
    [adopt],
  )

  const signOut = useCallback(async () => {
    await client.logout()
    setUser(null)
    setPermissions(new Set())
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      ready,
      user,
      permissions,
      lockedOut: user !== null && !permissions.has(P.adminRead),
      can: (...required: Permission[]) => required.every((p) => permissions.has(p)),
      signIn,
      signOut,
    }),
    [ready, user, permissions, signIn, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
